<template>
  <v-dialog :value="dialog" max-width="560" persistent>
    <v-card v-if="payslip">
      <v-form v-on:submit.prevent="sign">
        <v-card-title>
          <span class="headline">{{ readonly ? "Recibo firmado" : "Firmar recibo" }}</span>
        </v-card-title>

        <v-card-text class="text-left">
          <p class="mb-2">
            <b>{{ periodTitle(payslip) }}</b> · {{ payslip.type ? payslip.type.name : "" }}
          </p>
          <p v-if="readonly" class="mb-3">
            Firmado el <b>{{ formatDateTime(payslip.signed_at) }}</b>
          </p>
          <p v-else class="mb-3 grey--text text--darken-1">
            Si no estás de acuerdo, firmalo en disconformidad e indicá el motivo: igual queda registrado que lo recibiste.
          </p>

          <v-radio-group v-model="conformity" class="mt-0" row hide-details :disabled="readonly">
            <v-radio label="Conforme" :value="agree"></v-radio>
            <v-radio label="Disconforme" :value="disagree"></v-radio>
          </v-radio-group>

          <div v-if="conformity == disagree" class="mt-3">
            <v-textarea v-model="reason" label="Motivo de la disconformidad*" name="reason" data-vv-as="motivo"
              v-validate="readonly ? '' : 'required|max:1000'" rows="3" :disabled="readonly"></v-textarea>
            <span class="red--text">{{ errors.first("reason") }}</span>
          </div>

          <div class="mt-4">
            <v-label class="label-input">{{ readonly ? "Firma" : "Tu firma*" }}</v-label>
            <!-- Firmado: la firma guardada, solo para ver -->
            <div v-if="readonly" class="signature-view mt-2">
              <v-progress-circular v-if="loading_signature" indeterminate size="24" color="primary"></v-progress-circular>
              <img v-else-if="signature_image" :src="signature_image" alt="Firma del empleado" />
              <span v-else class="grey--text">Firmado sin firma dibujada.</span>
            </div>
            <template v-else>
              <SignaturePad ref="pad" v-model="signature" :error="signature_error" class="mt-2" />
              <span v-if="signature_error" class="red--text">Dibujá tu firma.</span>
            </template>
          </div>

          <div v-if="!readonly" class="mt-2">
            <v-text-field v-model="password" label="Tu contraseña*" name="password" data-vv-as="contraseña"
              v-validate="'required'" :type="show ? 'text' : 'password'" :append-icon="show ? 'mdi-eye' : 'mdi-eye-off'"
              @click:append="show = !show" autocomplete="current-password"></v-text-field>
            <span class="red--text">{{ errors.first("password") }}</span>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="error" text :disabled="loading" @click="close">{{ readonly ? "Cerrar" : "Cancelar" }}</v-btn>
          <v-btn v-if="!readonly" color="success" text type="submit" :loading="loading">Firmar</v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import SignaturePad from "@/components/SignaturePad.vue";
import { myPayslipService } from "@/libs/ws/myPayslipService";
import { PAYSLIP_STATUS_SIGNED, CONFORMITY_AGREE, CONFORMITY_DISAGREE, periodTitle, formatDateTime } from "@/constants/payslips";

// Firma del recibo (ya abierto): conformidad, firma dibujada y contraseña.
// Si ya esta firmado se muestra todo deshabilitado con la firma guardada (lo usan "Mis recibos" y el listado de RRHH).
export default {
  components: { SignaturePad },
  props: {
    dialog: Boolean,
    payslip: Object,
    // Funcion (id) => promesa con { signature } para ver la firma de un recibo firmado
    signatureLoader: Function,
  },
  data() {
    return {
      conformity: CONFORMITY_AGREE,
      reason: "",
      signature: null,
      signature_error: false,
      password: "",
      show: false,
      loading: false,
      signature_image: null,
      loading_signature: false,
      agree: CONFORMITY_AGREE,
      disagree: CONFORMITY_DISAGREE,
      periodTitle: periodTitle,
      formatDateTime: formatDateTime,
    };
  },
  computed: {
    readonly() {
      return this.payslip && this.payslip.status == PAYSLIP_STATUS_SIGNED;
    },
  },
  watch: {
    dialog(value) {
      if (!value) return;
      this.conformity = this.readonly ? this.payslip.conformity : CONFORMITY_AGREE;
      this.reason = this.readonly ? this.payslip.disagreement_reason || "" : "";
      this.signature_image = null;
      this.signature = null;
      this.signature_error = false;
      this.password = "";
      this.show = false;
      this.$validator.reset();
      if (this.readonly) {
        this.loadSignature();
        return;
      }
      // El canvas toma su tamaño cuando el dialogo ya se ve
      setTimeout(() => this.$refs.pad && this.$refs.pad.resize(), 250);
    },
    signature(value) {
      if (value) this.signature_error = false;
    },
  },
  methods: {
    loadSignature() {
      if (!this.signatureLoader) return;
      this.loading_signature = true;
      this.signatureLoader(this.payslip.id)
        .then((response) => {
          if (response.status) this.signature_image = response.result.signature;
        })
        .catch((e) => console.log(e))
        .finally(() => (this.loading_signature = false));
    },
    close() {
      this.$emit("updateDialog", false);
    },
    async sign() {
      if (this.loading) return;
      const valid = await this.$validator.validate();
      this.signature_error = !this.signature;
      if (!valid || this.signature_error) return;

      this.loading = true;
      myPayslipService
        .sign({
          id: this.payslip.id,
          password: this.password,
          conformity: this.conformity,
          reason: this.conformity == CONFORMITY_DISAGREE ? this.reason : null,
          signature: this.signature,
        })
        .then((response) => {
          if (response.status) {
            this.$toast.success(response.msg, "OK");
            this.close();
            this.$emit("signed", response.result);
          } else {
            this.$toast.error(response.msg, "Error");
          }
        })
        .catch((e) => {
          console.log(e);
          this.$toast.error("Ocurrió un error al firmar el recibo.", "Error");
        })
        .finally(() => (this.loading = false));
    },
  },
};
</script>

<style scoped>
.signature-view {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 160px;
  border: 1px solid #d5dcdf;
  border-radius: 4px;
  background: #fafafa;
}
.signature-view img {
  max-width: 100%;
  max-height: 150px;
}
</style>
